import {useParams} from "react-router-dom";
import React, {useEffect, useRef, useState} from "react";
import imgFallback from "../assets/images/image-fallback.webp";
import shareIcon from "../assets/icons/share-icon.svg";
import PropertySectionTab from "../components/PropertySectionTab";
import PropertySectionGallery from "../components/PropertySectionGallery";
import PropertySectionContact from "../components/PropertySectionContact";
import Slider from "react-slick";
import carouselLeftArrow from "../assets/icons/carousel-left-arrow.svg";
import carouselRightArrow from "../assets/icons/carousel-right-arrow.svg";
import {useTranslation} from "react-i18next";
import {Helmet} from "react-helmet-async";
import VideoPlayer from "../components/VideoPlayer";

export default function Property() {
    const {propertyId} = useParams();
    const [property, setProperty] = useState(null);
    const [loading, setLoading] = useState(true);
    const [videoTour, setVideoTour] = useState(false);
    const [showPlayer, setShowPlayer] = useState(false);
    const [isPlaying, setIsPlaying] = useState(false);
    const videoRef = useRef(null);

    const url = process.env.REACT_APP_API_URL;
    const sliderRef = useRef(null);
    const {t} = useTranslation();

    const handleShare = () => {
        if (navigator.share) {
            navigator.share({
                title: 'Downtown – Property',
                text: 'Scopri le migliori case di lusso in vendita con Downtown. Ville esclusive, attici panoramici e residenze uniche in location prestigiose. Trova la tua casa da sogno oggi stesso.',
                url: window.location.href,
            })
                .then()
                .catch((error) => console.error('Errore durante la condivisione:', error));
        } else {
            console.log('La condivisione non è supportata in questo browser.');
        }
    };

    const nextSlide = () => {
        sliderRef.current.slickNext();
    };

    const prevSlide = () => {
        sliderRef.current.slickPrev();
    };

    const settings = {
        dots: false,
        infinite: true,
        speed: 500,
        slidesToShow: 1,
        slidesToScroll: 1,
        arrows: false
    };

    useEffect(() => {
        async function getProperty() {
            const response = await fetch(`${url}/public-apis/property/${propertyId}`);
            const resData = await response.json();
            if (resData?.videos?.length > 0) {
                if (resData.videos[0].isResized) {
                    setVideoTour(true);
                }
            }
            setProperty(resData);
            setLoading(false);
        }

        getProperty();
    }, [propertyId, url]);

    useEffect(() => {
        if (process.env.REACT_APP_NODE_ENV === "production" && window.fbq) {
            window.fbq("track", "ViewContent", {
                content_name: `Property ${propertyId}`,
                content_ids: [propertyId],
                content_type: "property",
            });
        }
    }, [propertyId]);

    const handleOpen = () => {
        setShowPlayer(true);
    };

    const handleClose = () => {
        videoRef.current.pause();
        setIsPlaying(false);
        setShowPlayer(false);
    };

    const photoBgSrc = property?.photos?.length > 0 ? property.photos[0].path : imgFallback;

    if (loading) {
        return <div>Loading...</div>;
    }

    return (
        <div>
            <Helmet>
                <title>{`Downtown - ${t(`property.typologies.${property?.propertyTypology}`)} - ${property?.city}`}</title>
                <meta property="og:title"
                      content={`Downtown - ${t(`property.typologies.${property?.propertyTypology}`)} - ${property?.city}`}/>
                <meta property="og:description"
                      content='Scopri le migliori case di lusso in vendita con Downtown. Ville esclusive, attici panoramici e residenze uniche in location prestigiose. Trova la tua casa da sogno oggi stesso.'/>
                <meta property="og:url" content={window.location.href}/>
                <meta property="og:type" content="website"/>
            </Helmet>
            <section
                className="hidden md:block relative w-full max-w-[1920px] mx-auto"
                style={{aspectRatio: '16 / 9'}}
            >
                <div
                    className="absolute top-0 left-0 w-full h-full bg-cover bg-center"
                    style={{
                        backgroundImage: `
                         linear-gradient(0deg, #1A1A1A00 80%, #040506 98%),
                         linear-gradient(180deg, #1A1A1A00 0%, #040506 100%),
                    url(${photoBgSrc})`
                    }}
                ></div>
                <div className="box relative h-full flex items-end">
                    <div className="w-full px-[198px]">
                        <div className="flex justify-between items-end">
                            <div className="flex flex-col">
                                <span className={'font-serifDisplay text-[45px] text-lightGray capitalize'}>
                                        {t(`property.typologies.${property?.propertyTypology}`)}
                                </span>
                                <span
                                    className="font-raleway text-left text-[18px] tracking-[1.8px] text-gray uppercase">
                                    {property?.city}
                                </span>
                            </div>
                            {videoTour &&
                                <span onClick={handleOpen}
                                      className="font-raleway text-left text-[18px] tracking-[1.8px] text-gray uppercase cursor-pointer hidden lg:block">
                                    {t('property.videoTour')}
                                </span>
                            }
                            <div className="flex">
                                <span
                                    className={'font-raleway text-left text-[18px] tracking-[1.8px] text-gray uppercase flex'}>{property?.reference}</span>
                                <button
                                    className="font-raleway text-left text-[18px] tracking-[1.8px] text-gray uppercase flex"
                                    onClick={handleShare}>
                                    <img className="ms-4 mt-1" src={shareIcon} alt="share"/>
                                </button>
                            </div>
                        </div>
                        {videoTour &&
                            <div className={'flex justify-center items-center mt-14 lg:hidden'}>
                                <span onClick={handleOpen}
                                      className="font-raleway text-left text-[18px] tracking-[1.8px] text-gray uppercase cursor-pointer">
                                    {t('property.videoTour')}
                                </span>
                            </div>
                        }
                    </div>
                </div>
            </section>
            <section className="md:hidden mb-[122px]">
                <Slider ref={sliderRef} {...settings}>
                    {property?.photos.map((item, index) => {
                        return (
                            <div key={index}>
                                <img
                                    src={item.path}
                                    alt={`item-${index}`}
                                    className="w-full h-screen object-cover"
                                />
                            </div>
                        );
                    })}
                </Slider>
                <div
                    className="md:hidden absolute top-0 left-0 h-screen inset-0 bg-gradient-to-b from-[#040506a6] via-transparent to-[#040506]"
                />
            </section>
            <div className="md:hidden absolute right-10 bottom-[70px]">
                <button
                    onClick={prevSlide}
                    className="me-[10px]"
                >
                    <img src={carouselLeftArrow} alt="prevSlide"/>
                </button>
                <button
                    onClick={nextSlide}
                >
                    <img src={carouselRightArrow} alt="nextSlide"/>
                </button>
            </div>
            <PropertySectionTab
                property={property}
                handleClose={handleClose}
                isPlaying={isPlaying}
                setIsPlaying={setIsPlaying}
                videoRef={videoRef}
                setShowPlayer={setShowPlayer}
            />
            <PropertySectionGallery photos={property?.photos}/>
            <PropertySectionContact reference={property?.reference}/>

            {showPlayer &&
                <VideoPlayer
                    property={property}
                    handleClose={handleClose}
                    isPlaying={isPlaying}
                    setIsPlaying={setIsPlaying}
                    videoRef={videoRef}
                />}
        </div>
    )
}
